/**
 * GlobalView — Wildfire Detail Popup
 *
 * Overlay showing fire detection details for the selected wildfire entity.
 * Shows FRP (fire radiative power), confidence, location, time, satellite.
 * Draggable via header. Follows existing popup pattern (EarthquakePopup).
 */

import { useEffect, useRef } from 'react';
import { useGlobeStore } from '../../stores/globeStore';
import { useEntityStore } from '../../stores/entityStore';
import { confidenceToColor } from '../../services/firmsClient';
import { useDraggable } from '../../hooks/useDraggable';
import { useIsMobile } from '../../hooks/useIsMobile';
import EntityBottomSheet from './EntityBottomSheet';
import styles from './WildfirePopup.module.css';

// ─── Formatters ──────────────────────────────────────────────────

function timeAgo(isoStr: string): string {
  const diff = (Date.now() - new Date(isoStr).getTime()) / 1000;
  if (diff < 60) return `vor ${Math.round(diff)}s`;
  if (diff < 3600) return `vor ${Math.round(diff / 60)}min`;
  if (diff < 86400) return `vor ${(diff / 3600).toFixed(1)}h`;
  return `vor ${Math.round(diff / 86400)}d`;
}

function fmtTime(isoStr: string): string {
  const d = new Date(isoStr);
  return d.toLocaleString('de-CH', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'short',
  });
}

function fmtCoord(val: number, decimals = 4): string {
  return val.toFixed(decimals);
}

function confidenceLabel(confidence: string): string {
  switch (confidence) {
    case 'h': return 'HIGH';
    case 'n': return 'NOMINAL';
    case 'l': return 'LOW';
    default:  return 'UNKNOWN';
  }
}

function daynightLabel(dn: string): string {
  switch (dn) {
    case 'D': return '☀ Tag';
    case 'N': return '🌙 Nacht';
    default:  return dn || '—';
  }
}

// ─── Shared Content ──────────────────────────────────────────────

interface WildfireContentProps {
  entity: {
    position: { lat: number; lon: number; alt: number };
    timestamp: string;
    properties: Record<string, unknown>;
  };
}

function WildfireContent({ entity }: WildfireContentProps) {
  const props = entity.properties;
  const frp = (props.frp as number) ?? 0;
  const confidence = (props.confidence as string) ?? 'n';
  const brightness = props.brightness as number | undefined;
  const satellite = (props.satellite as string) ?? '—';
  const instrument = (props.instrument as string) ?? '—';
  const daynight = (props.daynight as string) ?? '';
  const scan = props.scan as number | undefined;
  const track = props.track as number | undefined;

  const confColor = confidenceToColor(confidence);

  return (
    <>
      {/* FRP badge */}
      <div className={styles.frpBadge}>
        <span
          className={styles.frpValue}
          style={{ color: confColor, textShadow: `0 0 12px ${confColor}` }}
        >
          {frp.toFixed(1)}
        </span>
        <div className={styles.frpMeta}>
          <span className={styles.frpLabel} style={{ color: confColor }}>FRP</span>
          <span className={styles.frpUnit}>MW (Fire Radiative Power)</span>
        </div>
      </div>

      {/* Confidence badge */}
      <span
        className={styles.confidenceBadge}
        style={{ borderColor: confColor, color: confColor }}
      >
        CONFIDENCE: {confidenceLabel(confidence)}
      </span>

      <div className={styles.section}>
        <div className={styles.sectionTitle}>ORT</div>
        <div className={styles.grid}>
          <Row label="LAT" value={fmtCoord(entity.position.lat)} suffix={entity.position.lat >= 0 ? '° N' : '° S'} />
          <Row label="LON" value={fmtCoord(entity.position.lon)} suffix={entity.position.lon >= 0 ? '° E' : '° W'} />
        </div>
      </div>

      <div className={styles.section}>
        <div className={styles.sectionTitle}>ZEIT</div>
        <div className={styles.grid}>
          <Row label="UTC" value={fmtTime(entity.timestamp)} />
          <Row label="AGO" value={timeAgo(entity.timestamp)} />
          <Row label="D/N" value={daynightLabel(daynight)} />
        </div>
      </div>

      <div className={styles.section}>
        <div className={styles.sectionTitle}>SENSOR</div>
        <div className={styles.grid}>
          <Row label="SAT" value={satellite} />
          <Row label="INST" value={instrument} />
          {brightness != null && <Row label="TEMP" value={`${brightness.toFixed(1)} K`} />}
          {scan != null && <Row label="SCAN" value={`${scan.toFixed(1)} km`} />}
          {track != null && <Row label="TRACK" value={`${track.toFixed(1)} km`} />}
        </div>
      </div>
    </>
  );
}

// ─── Component ───────────────────────────────────────────────────

export default function WildfirePopup() {
  const selectedEntityId = useGlobeStore((s) => s.selectedEntityId);
  const setSelectedEntity = useGlobeStore((s) => s.setSelectedEntity);
  const entity = useEntityStore((s) =>
    selectedEntityId ? s.entities.get(selectedEntityId) : undefined,
  );
  const isMobile = useIsMobile();

  const { style: dragStyle, onMouseDown, isDragging, reset } = useDraggable(296, 32);
  const prevEntityRef = useRef<string | null>(null);

  useEffect(() => {
    if (selectedEntityId && selectedEntityId !== prevEntityRef.current) {
      reset();
      prevEntityRef.current = selectedEntityId;
    }
  }, [selectedEntityId, reset]);

  useEffect(() => {
    if (!selectedEntityId) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSelectedEntity(null);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedEntityId, setSelectedEntity]);

  if (!selectedEntityId || !entity || entity.type !== 'wildfire') return null;

  const props = entity.properties as Record<string, unknown>;
  const frp = (props.frp as number) ?? 0;

  const handleClose = () => setSelectedEntity(null);

  const contentEntity = {
    position: entity.position,
    timestamp: entity.timestamp,
    properties: props,
  };

  const desktopPopup = (
    <div
      className={styles.popup}
      style={dragStyle}
      id="wildfire-popup"
      data-draggable
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div
        className={styles.header}
        onMouseDown={onMouseDown}
        style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
      >
        <div className={styles.headerLeft}>
          <span className={styles.headerIcon}>🔥</span>
          <span className={styles.headerTitle}>
            Wildfire — {frp.toFixed(1)} MW
          </span>
        </div>
        <button className={styles.closeBtn} onClick={handleClose} title="Schliessen (ESC)" id="wildfire-popup-close">✕</button>
      </div>
      <WildfireContent entity={contentEntity} />
    </div>
  );

  if (isMobile) {
    return (
      <EntityBottomSheet
        visible={true}
        entityName={`${frp.toFixed(1)} MW`}
        entitySub="NASA FIRMS Detection"
        icon="🔥"
        onClose={handleClose}
        desktopPopup={desktopPopup}
      >
        <WildfireContent entity={contentEntity} />
      </EntityBottomSheet>
    );
  }

  return desktopPopup;
}

// ─── Row Helper ──────────────────────────────────────────────────

function Row({ label, value, suffix }: { label: string; value: string; suffix?: string }) {
  return (
    <div className={styles.row}>
      <span className={styles.label}>{label}</span>
      <span className={styles.value}>
        {value}{suffix && <span className={styles.suffix}>{suffix}</span>}
      </span>
    </div>
  );
}
